import { format } from 'date-fns';
import React from 'react';
import { styled } from 'styled-components';

import { ActionMenuHeader } from 'components/ActionMenu';
import ActionMenu from 'components/ActionMenu/ActionMenu';
import ActionMenuList from 'components/ActionMenu/ActionMenuList';
import Avatar, { ArtistAvatar } from 'components/Avatar';
import TextHeader from 'components/TextHeader';
import Flex from 'components/Flex/Flex';
import {
  CalendarIcon,
  CheckmarkCircleIcon,
  EnvelopeIcon,
  SpotifyIcon,
  StarIcon
} from 'components/Icons';
import { COLORS } from 'config/constants';
import { ActionMenuItemProps } from 'components/ActionMenu/ActionMenuItem';
import { labelSubscriberAverageRating } from './helpers';
import { LabelSubscription } from './LabelSubscribersContext';

const iconPadding = 4;
const HeaderRowItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;

  // first HeaderRowItem should pad svg
  > ${HeaderRowItem}:first-child {
    svg {
      margin-right: ${iconPadding}px;
    }
  }
`;

const IconWithValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 32px;
  margin-left: 5px;
`;

const Value = styled.div`
  min-width: 14px;
  text-align: center;
`;

interface Props {
  subscription: LabelSubscription;
  actions: ActionMenuItemProps[];
  // action menu props
  isOpen: boolean;
  close: () => void;
  closeOnNavigation?: boolean;
}

const LabelSubscriberActionMenu = ({
  subscription,
  actions,
  // action menu props
  isOpen,
  close,
  closeOnNavigation
}: Props) => {
  const { node: artist, addedAt, isFollowing, averageRating } = subscription;

  const invitedOn = addedAt ? format(new Date(addedAt), 'd MMM yyyy') : '';

  return (
    <ActionMenu isOpen={isOpen} close={close} closeOnNavigation={closeOnNavigation}>
      <ActionMenuHeader>
        <div className="ListItem__content">
          {artist?.image && (
            <ArtistAvatar
              imageUrl={artist.image.url}
              size={Avatar.SIZES.LARGE}
              className="mr-1"
              lazy={false}
            />
          )}
          <Flex column>
            <TextHeader type={TextHeader.TYPES.MEDIUM} className="mb-0">
              {artist!.name}
            </TextHeader>
            <div>
              <HeaderRow>
                <HeaderRowItem>
                  <CalendarIcon size={12} /> Invited on {invitedOn}
                </HeaderRowItem>

                <HeaderRowItem>
                  Spotify Popularity{' '}
                  <IconWithValue>
                    <SpotifyIcon size={12} color="currentColor" />
                    <Value>{artist!.spotifyPopularity}</Value>
                  </IconWithValue>
                </HeaderRowItem>
              </HeaderRow>

              <HeaderRow style={{ marginTop: 3 }}>
                <HeaderRowItem>
                  {isFollowing ? (
                    <>
                      <CheckmarkCircleIcon
                        size={14}
                        color={COLORS.SUCCESS}
                        style={{ marginLeft: -1, marginRight: iconPadding - 1 }}
                      />
                      Invite Accepted
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon size={12} />
                      Invite Pending
                    </>
                  )}
                </HeaderRowItem>

                <HeaderRowItem>
                  Average Rating
                  <IconWithValue>
                    <StarIcon size={12} />
                    <Value>{labelSubscriberAverageRating(averageRating)}</Value>
                  </IconWithValue>
                </HeaderRowItem>
              </HeaderRow>
            </div>
          </Flex>
        </div>
      </ActionMenuHeader>

      <ActionMenuList actions={actions} closeMenu={close} />
    </ActionMenu>
  );
};

LabelSubscriberActionMenu.TEST_IDS = {
  FOLLOW_BUTTON: 'DropdownItem-follow',
  DISMISS_BUTTON: 'DropdownItem-dismiss',
  UNDO_BUTTON: 'DropdownItem-undo'
};

export default LabelSubscriberActionMenu;
